import { useCallback, useMemo, useReducer, useState } from 'react';

import { findExistingMembers, validateApiResponse } from '../../../services';
import { MEMBER_STATUS } from '../constants/copy.js';
import { INVITE_STEP } from '../constants/inviteRules.js';
import { canLeaveStep, getInviteSteps, getNextStep, getPreviousStep } from '../utils/inviteSteps.js';
import { parseEmails } from '../utils/parseEmails.js';
import { InviteMemberContext } from './InviteMemberContext.js';
import { initialInviteState, inviteMemberReducer } from './inviteMemberReducer.js';

const mapExisting = (m) => ({
  email: m.email,
  name: m.display_name ?? m.name ?? m.email,
  status: m.status === 'not_invited' ? MEMBER_STATUS.NOT_INVITED : m.status,
});

/**
 * Owns the wizard state of the Invite members builder and its actions. Everything derived
 * (step list, next / back target, gating, recipient count) is computed here from the state.
 */
function InviteMemberProvider({ children }) {
  const [state, dispatch] = useReducer(inviteMemberReducer, initialInviteState);
  const [lookupFailed, setLookupFailed] = useState(false);

  const addEmails = useCallback(async (raw) => {
    const { valid, invalid } = parseEmails(raw);
    if (valid.length === 0 && invalid.length === 0) return { added: 0, invalid: 0 };
    let existing = [];
    if (valid.length > 0) {
      try {
        const res = validateApiResponse(await findExistingMembers(valid));
        existing = res.body.items.map(mapExisting);
        setLookupFailed(false);
      } catch {
        // Advisory check: keep the addresses (the backend rejects real duplicates) and tell the user.
        setLookupFailed(true);
      }
    }
    // `valid` is lowercased by parseEmails; the API may answer with the stored casing.
    const existingEmails = new Set(existing.map((e) => e.email.toLowerCase()));
    const added = valid.filter((e) => !existingEmails.has(e));
    dispatch({ type: 'addEmails', valid: added, invalid, existing });
    return { added: added.length, invalid: invalid.length };
  }, []);

  const setDraft = useCallback((value) => dispatch({ type: 'setDraft', value }), []);

  /** Confirms what is typed in the field; resolves with how many addresses it added and how many were invalid. */
  const commitDraft = useCallback(async () => {
    const raw = state.draft;
    if (!raw.trim()) return { added: 0, invalid: 0 };
    dispatch({ type: 'setDraft', value: '' });
    return addEmails(raw);
  }, [state.draft, addEmails]);

  const correctInvalid = useCallback(
    async (value, corrected) => {
      dispatch({ type: 'removeInvalid', value });
      await addEmails(corrected);
    },
    [addEmails],
  );

  const removeEmail = useCallback((email) => dispatch({ type: 'removeEmail', email }), []);
  const removeInvalid = useCallback((value) => dispatch({ type: 'removeInvalid', value }), []);
  const removeExisting = useCallback((email) => dispatch({ type: 'removeExisting', email }), []);
  const setName = useCallback((email, name) => dispatch({ type: 'setName', email, name }), []);
  const setUserType = useCallback((userType) => dispatch({ type: 'setUserType', userType }), []);
  const toggleRole = useCallback((id) => dispatch({ type: 'toggleRole', id }), []);
  const toggleGroup = useCallback((id) => dispatch({ type: 'toggleGroup', id }), []);
  const setSendEmail = useCallback((value) => dispatch({ type: 'setSendEmail', value }), []);
  const setNote = useCallback((value) => dispatch({ type: 'setNote', value }), []);
  const goToStep = useCallback((step) => dispatch({ type: 'goToStep', step }), []);

  const { currentStep, userType } = state;
  const value = useMemo(() => {
    const steps = getInviteSteps(userType);
    const nextStep = getNextStep(userType, currentStep);
    const previousStep = getPreviousStep(userType, currentStep);
    return {
      state,
      steps,
      nextStep,
      previousStep,
      isLastStep: currentStep === INVITE_STEP.REVIEW,
      gate: canLeaveStep(currentStep, state),
      recipientCount: state.emails.length,
      lookupFailed,
      addEmails,
      setDraft,
      commitDraft,
      correctInvalid,
      removeEmail,
      removeInvalid,
      removeExisting,
      setName,
      setUserType,
      toggleRole,
      toggleGroup,
      setSendEmail,
      setNote,
      goToStep,
    };
  }, [
    state,
    lookupFailed,
    currentStep,
    userType,
    addEmails,
    setDraft,
    commitDraft,
    correctInvalid,
    removeEmail,
    removeInvalid,
    removeExisting,
    setName,
    setUserType,
    toggleRole,
    toggleGroup,
    setSendEmail,
    setNote,
    goToStep,
  ]);

  return <InviteMemberContext.Provider value={value}>{children}</InviteMemberContext.Provider>;
}

export default InviteMemberProvider;
